/* eslint-disable no-unused-vars */
import CardModal from '../CardModal';
import {
  createCardOptionsAction,
  getCardOptionsAction,
  getCardOptionsReset,
  updateCardOptionsAction,
} from 'actions/storeActions';
import React from 'react';
import {
  setAccountDefaultButton,
  setAddEditFavouritesButtons,
  setEditFavouritesButtons,
} from 'actions/modalMetaActions';
import { getAddEditFavouriteRequest } from 'features/cards/helpers/add-edit-card-helper';
import Vas from 'constants/vas-codes';
import { getCardAccountDefaultRequest } from 'actions/cardActions';
import { getFleetLimiterCodesObj } from 'features/cards/helpers/add-edit-card-helper';
import { Form, Loading } from '@ampol/design-system';
import useCardModalController from '../useCardModalController';
import { useEffect, useState } from 'react';
import {
  useDispatchCardFormValues,
  useDispatchCardModalMeta,
  useDispatchResetCardFormValues,
} from 'dispatchers/dispatchers';
import { CardModalFormNames, CardModalFormValues } from '../types/CardModalFormNames';
import {
  useSelectAccountDefaults,
  useSelectAccountDefaultsLoading,
  useSelectAssignedGrandfatherClientNumber,
  useSelectCardFormValues,
  useSelectCardModalMeta,
  useSelectCardOptionDetails,
  useSelectCardOptions,
} from 'selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { getCardUsageOptionsSchema } from '../types/CardModalValidationSchema';

interface AccountFavouriteEditFormProps {
  contractNumber: string;
  modalType?: string;
}

const EDIT_FAVOURITES_FORM_ID = 'card-usage-options-account-detail';

enum EdittingType {
  NONE,
  ADD,
  EDIT,
}

export default function AccountFavouriteEditForm({ contractNumber, modalType }: AccountFavouriteEditFormProps) {
  const dispatch = useDispatch();
  const dispatchCardFormValues = useDispatchCardFormValues();
  const dispatchCardModalMeta = useDispatchCardModalMeta();
  const dispatchResetCardFormValues = useDispatchResetCardFormValues();

  const cardOptions = useSelectCardOptions();
  const formValues = useSelectCardFormValues();
  const isDefaultsLoading = useSelectAccountDefaultsLoading();
  const assignedGrandfatherClientNumber = useSelectAssignedGrandfatherClientNumber();
  const accountDefaults = useSelectAccountDefaults();
  const cardOptionDetails = useSelectCardOptionDetails();
  const meta = useSelectCardModalMeta();

  const createCardOptionSuccess = useSelector((state: any) => state.createCardOptions.isSuccess);
  const updateCardOptionSuccess = useSelector((state: any) => state.updateCardOptions.isSuccess);
  const canReadWrite = useSelector((state: any) => state.userStore.canReadWriteAccounts);

  const [editting, setEditting] = useState(false);
  const [edittingType, setEdittingType] = useState(EdittingType.NONE);
  const [currentlyEditingFavouriteID, setCurrentlyEditingFavourite] = useState<string>(undefined);
  const [currentlyEditingFavouriteName, setCurrentlyEditingFavouriteName] = useState<string>(undefined);

  const currentFavouriteId = formValues?.[CardModalFormNames.Favourite];
  const currentFavouriteName = cardOptions?.find((item) => item.id === currentFavouriteId)?.favouriteName;
  const fleetLimitersCodes = meta?.fleetLimiters?.map((item) => item.code);
  const favourites = cardOptions?.map((obj) => obj.favouriteName);
  const favouriteMinusCurrent = favourites?.filter((item) => item !== currentlyEditingFavouriteName);
  const validationSchema = getCardUsageOptionsSchema(fleetLimitersCodes, favouriteMinusCurrent);

  function setMetaFromDefaults(meta = {}) {
    dispatchCardModalMeta({
      dailyDollarLimits: accountDefaults.dailyDollarLimits,
      monthlyDollarLimits: accountDefaults.monthlyDollarLimits,
      fleetLimiters: accountDefaults.fleetLimiters,
      vasRsaAccountCode: accountDefaults.vasRsaAccountCode,
      vasSnrAccountCode: accountDefaults.vasSnrAccountCode,
      ...meta, // allows override
    });
  }

  // Handles updating values live on success
  useEffect(() => {
    if (contractNumber) {
      dispatch(getCardOptionsReset());
      dispatch(getCardOptionsAction(contractNumber, assignedGrandfatherClientNumber));
    }
  }, [createCardOptionSuccess, updateCardOptionSuccess]);

  // Handles enabling/disabling the edit button
  useEffect(() => {
    if (formValues?.[CardModalFormNames.Favourite] && !editting && canReadWrite) {
      dispatch(
        setEditFavouritesButtons({
          onClick: handleEditFavouriteClick,
        })
      );
    }
  }, [formValues]);

  // Handle retrieving account defaults on load & cleanup
  useEffect(() => {
    if (!contractNumber) return;
    refreshAccountDefaults();
    return () => {
      handleCancelClick();
      dispatchResetCardFormValues();
    };
  }, [contractNumber]);

  // Set initial form values to account defaults and meta defaults
  useEffect(() => {
    if (!accountDefaults) return;
    setMetaFromDefaults();
    resetToAccountDefaults();
  }, [accountDefaults]);

  function refreshAccountDefaults() {
    dispatch(
      getCardAccountDefaultRequest({
        contractNumber: contractNumber,
        grandParentAccountNumber: assignedGrandfatherClientNumber,
      })
    );
  }

  const handleCancelClick = () => {
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Card_Favourites_Cancel',
      selected: true,
    });
    setEditting(false);
    dispatch(setAccountDefaultButton());
    setMetaFromDefaults();
    resetToAccountDefaults();
    setCurrentlyEditingFavourite(undefined);
    setCurrentlyEditingFavouriteName(undefined);
  };

  function handleAddNewFavouriteClick() {
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Card_Favourites_Add_New_Favourite',
      selected: true,
    });
    setEditting(true);
    setEdittingType(EdittingType.ADD);
    setMetaFromDefaults({ verifyPosPrompt: accountDefaults.verifyPosPrompt });
    dispatch(
      setAddEditFavouritesButtons({
        formId: EDIT_FAVOURITES_FORM_ID,
        onCancelClick: handleCancelClick,
      })
    );
    resetToAccountDefaults();
  }

  const handleEditFavouriteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Card_Favourites_Edit',
      selected: true,
    });
    e.preventDefault();
    setCurrentlyEditingFavourite(currentFavouriteId);
    setCurrentlyEditingFavouriteName(currentFavouriteName);
    setEditting(true);
    setEdittingType(EdittingType.EDIT);
    if (accountDefaults.verifyPosPrompt === 'Mandatory PIN' && formValues.verifyPosPrompt === 'Mandatory PIN') {
      setMetaFromDefaults({ verifyPosPrompt: accountDefaults.verifyPosPrompt });
    }
    dispatch(
      setAddEditFavouritesButtons({
        formId: EDIT_FAVOURITES_FORM_ID,
        inactive: false,
        onCancelClick: handleCancelClick,
      })
    );

    dispatchCardFormValues({
      cardFavoriteName: currentFavouriteName,
    });
  };

  function resetToAccountDefaults() {
    const formBlock = document?.getElementById(EDIT_FAVOURITES_FORM_ID) as HTMLFormElement;
    formBlock?.reset();
    dispatchResetCardFormValues();
    dispatchCardFormValues({
      ...accountDefaults,
      ...getFleetLimiterCodesObj(accountDefaults.fleetLimiters),
      [CardModalFormNames.AllProducts]: accountDefaults.fleetLimiters?.every(
        (item) => item.available === false || item.selected === true
      ),
      [CardModalFormNames.FuelPay]:
        accountDefaults.vasFuelPayAccountCode !== Vas.FP_YA && accountDefaults.vasFuelPayAccountCode !== Vas.FP_NC,
    });
  }

  function handleSubmitAddFavourite(values: CardModalFormValues) {
    const request = getAddEditFavouriteRequest(values, contractNumber, accountDefaults.fleetLimiters);
    dispatch(createCardOptionsAction(request, assignedGrandfatherClientNumber));
    handleCancelClick();
  }

  function handleSubmitEditFavourite(values: CardModalFormValues) {
    // Here we inject the currently editing favourite ID into the form values (this could be improved)
    // But this way keeps the previous getAddEditFavouriteRequest function working
    // (yes it was originally spelt wrong without the 'u' in favourite)
    values['accountFavorites'] = currentlyEditingFavouriteID;
    const request = getAddEditFavouriteRequest(
      values,
      contractNumber,
      accountDefaults.fleetLimiters,
      cardOptionDetails.id
    );
    dispatch(updateCardOptionsAction(request, assignedGrandfatherClientNumber));
    handleCancelClick();
  }

  function onSubmitForm(values: CardModalFormValues) {
    switch (edittingType) {
      case EdittingType.ADD:
        handleSubmitAddFavourite(values);
        break;
      case EdittingType.EDIT:
        handleSubmitEditFavourite(values);
        break;
    }
  }

  const controller = useCardModalController(modalType, undefined, contractNumber);
  return (
    <Form
      key={editting ? 'edit-mode' : 'view-mode'}
      id={EDIT_FAVOURITES_FORM_ID}
      schema={validationSchema}
      defaultValues={controller.defaultValues}
      onChange={controller.onFormChange}
      onSubmit={onSubmitForm}
      memoizeDefaultValues={false}
    >
      <div className='space-y-xl'>
        <CardModal.UsageOptions
          contractNumber={contractNumber}
          favouriteMode='manage'
          readOnly={!editting}
          hideFavourites={editting}
          showFavouriteAddButton={!editting}
          showFavouriteNameInput={editting}
          onClickAddFavourite={handleAddNewFavouriteClick}
          isFavouritesLoading={isDefaultsLoading}
        />
        {isDefaultsLoading && <Loading />}
        {!isDefaultsLoading && <CardModal.ProductsAndServices readOnly={!editting} />}
      </div>
    </Form>
  );
}
